import React, { useState, useEffect } from 'react';
import { Typography, Button, Row, Col, DatePicker, Modal } from 'antd';
import { FullscreenOutlined, UnorderedListOutlined } from '@ant-design/icons';
import { ResponsiveContainer, BarChart, CartesianGrid, XAxis, YAxis, Tooltip, Legend, Bar, Label } from 'recharts';
import moment from 'moment';

import { useSelector } from 'react-redux';

import { geti18nText, NyRequestResolver, NySpinner, NyUtils, RESPONSE } from '@nybble/nyreact';
import { RootState } from '../../../rootReducer';
import { CONSTANTS_REQ, DeviceEnergySettings, DeviceEnergySettingsByPhase } from '../../../utils/Constants';

const { Title } = Typography;
const { RangePicker } = DatePicker;

interface ICharEnergySpan {
    phases?: 'L1' | 'L2' | 'L3' | 'ALL';
    nameL1?: any;
    nameL2?: any;
    nameL3?: any;
}

const CharEnergySpan: React.FC<ICharEnergySpan> = ({ phases = 'ALL', nameL1 = 'L1', nameL2 = 'L2', nameL3 = 'L3' }) => {
    const { record } = useSelector((state: RootState) => state.deviceEnergyDetails);

    const [params, setParams] = useState<any>({});
    const [graphData, setGraphData] = useState<any>(null);
    const [loading, setLoading] = useState(false);
    const [visible, setVisible] = useState(false);
    const [visibleDataVolumes, setVisibleDataVolumes] = useState(false);
    const [visibleDataButton, setVisibleDataButton] = useState(false);

    const [energySettings, setEnergySettings] = useState<any>(DeviceEnergySettings);

    useEffect(() => {
        setEnergySettings(DeviceEnergySettingsByPhase(nameL1, nameL2, nameL3));
    }, [nameL1, nameL2, nameL3]);

    useEffect(() => {
        if (params && params.period) {
            fetch();
        }
    }, [params]);

    useEffect(() => {
        if (params && params.period) {
            fetch();
        }
    }, [params]);

    const fetch = () => {
        setGraphData([]);
        if (
            params &&
            ((params.period !== 'initial' && params.period !== 'span') ||
                (params.period === 'initial' && params.deviceId !== null) ||
                (params.period === 'span' && params.timestampFrom !== null && params.timestampTo !== null))
        ) {
            setVisibleDataButton(true);
            setLoading(true);
            NyRequestResolver.requestGet(CONSTANTS_REQ.DEVICE.CHARTS_ENERGY_CONSUMPTION, {
                ...params,
                lang: NyUtils.getSelectedLanguage(),
                deviceId: record.id,
            }).then((response: any) => {
                setLoading(false);
                if (response.status === RESPONSE.OK) {
                    setGraphData(response.data);
                }
            });
        } else {
            setVisibleDataButton(false);
        }
    };

    function onRangeChange(dates: any) {
        const timestampFrom = dates[0] ? moment(dates[0]).valueOf() : null;
        const timestampTo = dates[1] ? moment(dates[1]).valueOf() : null;
        setParams((prevState: any) => {
            return { ...prevState, period: 'span', timestampFrom: timestampFrom, timestampTo: timestampTo };
        });
    }

    function disabledDate(current: any) {
        const rangeStart = moment().subtract(1, 'years');
        const rangeEnd = moment().endOf('day');
        return !(rangeStart.isSameOrBefore(current) && rangeEnd.isAfter(current));
    }

    return (
        <div className="box" style={{ height: '95%' }}>
            <Title level={4}>
                {geti18nText('deviceDetails.graph.light.title') + ' - ' + geti18nText('report.time_range.span')}
            </Title>
            {visibleDataButton && (
                <Button
                    style={{ position: 'absolute', right: '50px', top: '20px' }}
                    shape="circle"
                    onClick={() => setVisibleDataVolumes(true)}
                    icon={<UnorderedListOutlined />}
                />
            )}
            <Button
                style={{ position: 'absolute', right: '20px', top: '20px' }}
                shape="circle"
                icon={<FullscreenOutlined />}
                onClick={() => setVisible(true)}
            />
            <Row gutter={24} style={{ marginBottom: '10px' }}>
                <Col span={24}>
                    <RangePicker
                        disabledDate={disabledDate}
                        showTime
                        format="DD.MM.YYYY HH:mm:ss"
                        onChange={onRangeChange}
                    />
                </Col>
            </Row>
            <ResponsiveContainer width="100%" height={212}>
                {loading ? (
                    <NySpinner />
                ) : (
                    <BarChart data={graphData}>
                        <CartesianGrid strokeDasharray="3 3" vertical={false} />
                        <XAxis dataKey="converted" />
                        <YAxis>
                            <Label
                                value={geti18nText('deviceDetails.graph.light.y.value')}
                                position="insideLeft"
                                angle={-90}
                                style={{ textAnchor: 'middle' }}
                            />
                        </YAxis>
                        {graphData && <Tooltip />}
                        <Legend />
                        {(phases === 'ALL' || phases === 'L1') && (
                            <Bar dataKey="barValue" name={energySettings['y-title']} fill={energySettings['color']} />
                        )}
                        {(phases === 'ALL' || phases === 'L2') && (
                            <Bar
                                dataKey="barValue1"
                                name={energySettings['y-title1']}
                                fill={energySettings['color1']}
                            />
                        )}
                        {(phases === 'ALL' || phases === 'L3') && (
                            <Bar
                                dataKey="barValue2"
                                name={energySettings['y-title2']}
                                fill={energySettings['color2']}
                            />
                        )}
                    </BarChart>
                )}
            </ResponsiveContainer>

            <Modal
                title={geti18nText('deviceDetails.graph.light.title') + ' - ' + geti18nText('report.time_range.span')}
                visible={visible}
                onOk={() => setVisible(false)}
                onCancel={() => setVisible(false)}
                width={'80%'}
                zIndex={1010}
                centered={true}
            >
                <Row gutter={24} style={{ marginBottom: '10px' }}>
                    <Col span={24}>
                        <RangePicker
                            disabledDate={disabledDate}
                            showTime
                            format="DD.MM.YYYY HH:mm:ss"
                            onChange={onRangeChange}
                        />
                    </Col>
                </Row>
                <ResponsiveContainer width="100%" height={600}>
                    {loading ? (
                        <NySpinner />
                    ) : (
                        <BarChart data={graphData}>
                            <CartesianGrid strokeDasharray="3 3" vertical={false} />
                            <XAxis dataKey="converted" />
                            <YAxis
                                label={{
                                    value: geti18nText('deviceDetails.graph.light.y.value'),
                                    angle: -90,
                                    position: 'insideLeft',
                                }}
                            />
                            {graphData && <Tooltip />}
                            <Legend />
                            {(phases === 'ALL' || phases === 'L1') && (
                                <Bar
                                    dataKey="barValue"
                                    name={energySettings['y-title']}
                                    fill={energySettings['color']}
                                />
                            )}
                            {(phases === 'ALL' || phases === 'L2') && (
                                <Bar
                                    dataKey="barValue1"
                                    name={energySettings['y-title1']}
                                    fill={energySettings['color1']}
                                />
                            )}
                            {(phases === 'ALL' || phases === 'L3') && (
                                <Bar
                                    dataKey="barValue2"
                                    name={energySettings['y-title2']}
                                    fill={energySettings['color2']}
                                />
                            )}
                        </BarChart>
                    )}
                </ResponsiveContainer>
            </Modal>
            <Modal
                visible={visibleDataVolumes}
                onCancel={() => setVisibleDataVolumes(false)}
                width={'80%'}
                centered={true}
                footer={null}
            >
                {/* <ChartDataConsumption
                    title={geti18nText('report.time_range.span')}
                    deviceId={record.id}
                    period={params}
                /> */}
            </Modal>
        </div>
    );
};

export default CharEnergySpan;
